import { createTheme } from "@mui/material";
import { CITYSCAPE_HEIGHT } from "src/components/CityscapeBackdrop";

function fadeGradient(px) {
    return `linear-gradient(to bottom, transparent 0px, transparent ${px}px, rgba(0,0,0,0.18) ${px}px, rgba(0,0,0,0.28) 100%)`;
}

// Single fixed brand theme: betonijerry, dark. The former light/dark mode
// switching and the obsolete "kalle" brand (plus ThemeModeProvider / VITE_BRAND)
// were removed — the app now ships one theme with no user-facing switcher.
const pageBg = "#1A2F38";
const cardBg = "#243842";
const textPrimary = "#F4F2EE";
const textSecondary = "#B8C9D2";
const dividerColor = "#3A5060";

// Amber CTA tokens — single source reused by the MuiButton "warning" variant
// below and by custom.wizard (also read by amberCtaSx.js via theme.custom.wizard).
const amberGradient = "linear-gradient(180deg, #E8893B 0%, #D87B30 100%)";
const amberShadow = "0 8px 24px rgba(232,137,59,0.35)";
const amberShadowHover = "0 11px 28px rgba(232,137,59,0.40)";
const amberMotion = { easing: "cubic-bezier(0.22, 1, 0.36, 1)", focus: "160ms", toggle: "220ms", entrance: "360ms" };
// Hover lifts via light, not geometry — only the amber glow swells (box-shadow),
// the button never moves. A sub-pixel position nudge reads as a twitch no matter
// how slow it's eased, so elevation is implied by the deepening shadow alone.
const amberTransition = `box-shadow ${amberMotion.toggle} ${amberMotion.easing}`;

export const theme = createTheme({
    palette: {
        mode: "dark",
        primary: { main: "#3A8A9E", dark: "#2C5566", contrastText: "#F4F2EE" },
        secondary: { main: "#2C5566", contrastText: "#F4F2EE" },
        warning: { main: "#E8893B", contrastText: "#1A1A1A" },
        text: { primary: textPrimary, secondary: textSecondary },
        background: { default: pageBg, paper: cardBg },
        divider: dividerColor,
    },
    typography: {
        fontFamily: "'Inter', system-ui, -apple-system, Segoe UI, Roboto, sans-serif",
        h1: { fontFamily: "'Black Ops One', serif" },
        h2: { fontFamily: "'Black Ops One', serif" },
        h3: { fontFamily: "'Black Ops One', serif" },
        h4: { fontFamily: "'Black Ops One', serif" },
    },
    shape: { borderRadius: 6 },
    components: {
        MuiButtonBase: {
            styleOverrides: {
                root: {
                    "&:focus-visible": {
                        outline: "3px solid #FFD23F",
                        outlineOffset: 2,
                    },
                },
            },
        },
        MuiAppBar: { defaultProps: { elevation: 0 } },
        MuiTextField: { defaultProps: { variant: "outlined", size: "small" } },
        // Cap every Select menu's height so it opens *below* the field in normal
        // scroll positions. A tall menu can't fit under a mid-page field, so MUI
        // flips it up over the cursor — and Select's open-on-mousedown drag-select
        // then ghost-selects the option under the pointer on release (only a 400ms
        // guard window, which the flip-up + Grow animation outlasts). 400 keeps long
        // lists scrollable while usually still fitting below the field. NOTE: the two rich
        // fields (RasitusluokkaField / KayttokohdeField) pass their own MenuProps,
        // which fully replaces this default — their maxHeight is set in-component.
        MuiSelect: {
            defaultProps: {
                MenuProps: { slotProps: { paper: { sx: { maxHeight: 400 } } } },
            },
        },
        MuiCssBaseline: {
            styleOverrides: { body: { backgroundColor: pageBg } },
        },
        MuiButton: {
            defaultProps: { disableElevation: true },
            // MUI v9 dropped the `${variant}${Color}` styleOverrides slots, so the
            // amber-glow CTA look is delivered via a `variants` entry — applied
            // after styleOverrides + the built-in disableElevation (so the glow
            // wins) and before `sx` (so sx={amberCtaSx} buttons stay identical).
            // Every contained warning button glows.
            variants: [
                // Contained PRIMARY: off-white on #3A8A9E measured 3.5:1 (fails AA
                // 4.5:1) and it was the only CTA in two esikatselu states. A lighter
                // teal with dark ink is 6:1; hover lightens rather than darkens so
                // the ink keeps its contrast. palette.primary itself is untouched:
                // heroTextColor reads primary.contrastText for the landing hero.
                {
                    props: { variant: "contained", color: "primary" },
                    style: {
                        backgroundColor: "#4FA3B8",
                        color: "#0F1A1F",
                        "&:hover": { backgroundColor: "#4FA3B8", filter: "brightness(1.08)" },
                    },
                },
                {
                    props: { variant: "contained", color: "warning" },
                    style: {
                        fontWeight: 700,
                        textTransform: "uppercase",
                        background: amberGradient,
                        boxShadow: `inset 0 1px 0 rgba(255,255,255,0.20), ${amberShadow}`,
                        transition: amberTransition,
                        "&:hover": {
                            background: amberGradient,
                            boxShadow: `inset 0 1px 0 rgba(255,255,255,0.20), ${amberShadowHover}`,
                        },
                        // Inert grey, in sync with amberCtaSx.js — a dimmed amber
                        // gradient read as a muted-but-still-clickable button.
                        "&.Mui-disabled": {
                            background: "rgba(244,242,238,0.10)",
                            color: "rgba(244,242,238,0.40)",
                            boxShadow: "none",
                        },
                        "@media (prefers-reduced-motion: reduce)": {
                            transition: "none",
                        },
                    },
                },
            ],
        },
        MuiCard: {
            defaultProps: { elevation: 0 },
            styleOverrides: {
                root: {
                    backgroundColor: cardBg,
                    border: `1px solid ${dividerColor}`,
                },
            },
        },
        MuiOutlinedInput: {
            styleOverrides: {
                root: ({ theme }) => {
                    const w = theme.custom?.wizard ?? {};
                    const m = w.motion ?? {};
                    const focus = m.focus ?? "160ms";
                    const easing = m.easing ?? "ease-out";
                    const trs = `${focus} ${easing}`;
                    return {
                        borderRadius: w.fieldRadius ?? 10,
                        transition: `border-color ${trs}, box-shadow ${trs}`,
                        "&.Mui-focused": {
                            boxShadow: `0 0 0 4px ${w.amberGlow ?? "rgba(232,137,59,0.15)"}`,
                        },
                        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                            borderColor: w.amber ?? "#E8893B",
                            borderWidth: 1.5,
                        },
                        "@media (prefers-reduced-motion: reduce)": {
                            transition: "none",
                        },
                    };
                },
            },
        },
    },
    custom: {
        brand: "betonijerry",
        pageTexture: "url(/textures/concrete-grunge.svg)",
        pageTextureSize: "400px 400px",
        pageTextureBlend: "normal",
        pageTextureOpacity: 1,
        appBar: { bg: "#2C5566", fg: "#F4F2EE" },
        wordmark: {
            text: "BETONIJERRY",
            fontFamily: '"Black Ops One", serif',
            letterSpacing: "0.05em",
            textShadow: "2px 2px 0 rgba(0, 0, 0, 0.25)",
            fontWeight: undefined,
            textTransform: undefined,
        },
        heroTextColor: "primary.contrastText",
        heroAccent: undefined,
        heroBackdropGradient: {
            xs: fadeGradient(CITYSCAPE_HEIGHT.xs),
            sm: fadeGradient(CITYSCAPE_HEIGHT.sm),
            md: "none",
        },
        mascotFilter: "none",
        wizard: {
            cardBg:        "rgba(36,56,66,0.62)",
            cardBlur:      "blur(28px) saturate(150%)",
            cardBorder:    "1px solid rgba(244,242,238,0.08)",
            cardShadow:    "inset 0 1px 0 rgba(255,255,255,0.04), 0 24px 64px rgba(0,0,0,0.45), 0 6px 18px rgba(0,0,0,0.30)",
            cardRadius:    24,
            cardBgFallback:"rgba(36,56,66,0.92)",
            microBg:       "rgba(36,56,66,0.65)",
            microBlur:     "blur(16px) saturate(140%)",
            microRadius:   14,
            fieldRadius:   10,
            amber:         "#E8893B",
            amberDeep:     "#D87B30",
            amberLight:    "#FFB36B",
            amberGlow:     "rgba(232,137,59,0.15)",
            amberGradient,
            amberShadow,
            amberShadowHover,
            motion: amberMotion,
            stateTints: {
                success: { title: "#7BC598", discFill: "rgba(123,197,152,0.15)", discBorder: "rgba(123,197,152,0.40)" },
                warn:    { title: "#E8C257", discFill: "rgba(232,194,87,0.14)",  discBorder: "rgba(232,194,87,0.40)"  },
                error:   { title: "#E07A6B", discFill: "rgba(224,122,107,0.14)", discBorder: "rgba(224,122,107,0.40)" },
            },
        },
    },
});
